import React, { useEffect, useState } from 'react';

type ContentBlockSelector = {
  selector: string;
  threshold?: number;
};

const defaultContentBlockSelectors: ContentBlockSelector[] = [
  // Target entire content blocks/sections
  { selector: '.rt-Container, .rt-Flex, .rt-Box, .rt-Grid', threshold: 0.15 },
  // Target card and panel elements that contain content
  {
    selector: '.rt-Card, [class*="section"], [class*="panel"]',
    threshold: 0.15,
  },
  // Target entire form blocks and content blocks
  { selector: '.rt-Form, .content-block, .feature-block', threshold: 0.15 },
];

export const AnimationWrapper: React.FC<{
  children: React.ReactNode;
  contentBlockSelectors?: ContentBlockSelector[];
  enabled?: boolean;
}> = ({
  children,
  contentBlockSelectors = defaultContentBlockSelectors,
  enabled = true,
}) => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsClient(true);
    }
  }, []);

  useEffect(() => {
    if (!isClient || !enabled) return;

    // Function to animate content blocks
    const animateContentBlocks = () => {
      // Create a Set to track elements we've processed to avoid duplicates
      const processedElements = new Set();

      contentBlockSelectors.forEach(({ selector, threshold = 0.15 }) => {
        const contentBlocks = document.querySelectorAll(selector);

        contentBlocks.forEach((block) => {
          // Skip if already processed or if it's an image/background element
          if (
            processedElements.has(block) ||
            block.tagName === 'IMG' ||
            block.classList.contains('no-animate') ||
            block.classList.contains('animation-processed')
          ) {
            return;
          }

          // Find all text elements within this block
          const textElements = block.querySelectorAll(
            'h1, h2, h3, h4, h5, h6, p, ul, ol, .rt-Heading, .rt-Text',
          );

          // Only animate blocks that contain text
          if (textElements.length > 0) {
            // Mark block as processed
            block.classList.add('animation-processed');
            processedElements.add(block);

            // Add animation to the entire block
            block.classList.add('opacity-0');

            const observer = new IntersectionObserver(
              (entries) => {
                entries.forEach((entry) => {
                  if (entry.isIntersecting) {
                    // Animate the entire block at once
                    entry.target.classList.remove('opacity-0');
                    entry.target.classList.add('animate-fade-in-bottom');

                    // Once animated, stop observing
                    observer.unobserve(entry.target);
                  }
                });
              },
              { threshold, rootMargin: '0px 0px -50px 0px' },
            );

            observer.observe(block);
          }
        });
      });
    };

    // Initial animation
    animateContentBlocks();

    // Set up mutation observer to handle dynamically added content
    const mutationObserver = new MutationObserver((mutations) => {
      let shouldAnimate = false;

      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          shouldAnimate = true;
        }
      });

      if (shouldAnimate) {
        animateContentBlocks();
      }
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      mutationObserver.disconnect();
    };
  }, [isClient, contentBlockSelectors, enabled]);

  return <>{children}</>;
};

export default AnimationWrapper;
